/**
 * useFilteredMistakes Hook
 * Applies filters to a list of mistakes and provides grouped data
 */

import { useMemo } from 'react';
import type { Mistake } from '../../../lib/api';
import type { MistakeFilters, MistakeGroup } from '../services/mistakeFilterModel';
import {
  applyMistakeFilters,
  calculateMistakeStats,
  groupMistakesByField,
  getFilterOptions,
} from '../services/mistakeFilterModel';

export interface UseFilteredMistakesReturn {
  filteredMistakes: Mistake[];
  stats: ReturnType<typeof calculateMistakeStats>;
  examTypeOptions: Array<{ value: string; label: string; count: number }>;
  sectionOptions: Array<{ value: string; label: string; count: number }>;
  difficultyOptions: Array<{ value: string; label: string; count: number }>;
  examTypeGroups: MistakeGroup[];
  sectionGroups: MistakeGroup[];
  difficultyGroups: MistakeGroup[];
}

export function useFilteredMistakes(
  mistakes: Mistake[],
  filters: MistakeFilters
): UseFilteredMistakesReturn {
  const filteredMistakes = useMemo(
    () => applyMistakeFilters(mistakes, filters),
    [mistakes, filters]
  );

  const stats = useMemo(() => calculateMistakeStats(mistakes), [mistakes]);

  const examTypeOptions = useMemo(() => getFilterOptions(mistakes, 'examType'), [mistakes]);

  const sectionOptions = useMemo(() => getFilterOptions(mistakes, 'section'), [mistakes]);

  const difficultyOptions = useMemo(() => getFilterOptions(mistakes, 'difficulty'), [mistakes]);

  const examTypeGroups = useMemo(() => groupMistakesByField(mistakes, 'examType'), [mistakes]);

  const sectionGroups = useMemo(() => groupMistakesByField(mistakes, 'section'), [mistakes]);

  const difficultyGroups = useMemo(() => groupMistakesByField(mistakes, 'difficulty'), [mistakes]);

  return {
    filteredMistakes,
    stats,
    examTypeOptions,
    sectionOptions,
    difficultyOptions,
    examTypeGroups,
    sectionGroups,
    difficultyGroups,
  };
}
